import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import DashboardLayout from "./components/layout/DashboardLayout";
import { lazy, Suspense } from "react";

// Lazy load all pages - only download when navigated to
const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage"));
const RevenuePage = lazy(() => import("./pages/RevenuePage"));
const BankingPage = lazy(() => import("./pages/BankingPage"));
const BudgetsPage = lazy(() => import("./pages/BudgetsPage"));
const CostCentersPage = lazy(() => import("./pages/CostCentersPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const AIAssistantPage = lazy(() => import("./pages/AIAssistantPage"));
const AIHubPage = lazy(() => import("./pages/AIHubPage"));
const MessagingPage = lazy(() => import("./pages/MessagingPage"));
const VendorsCustomersPage = lazy(() => import("./pages/VendorsCustomersPage"));
const ChartOfAccountsPage = lazy(() => import("./pages/ChartOfAccountsPage"));
const TransactionsPage = lazy(() => import("./pages/TransactionsPage"));
const PaymentRequestsPage = lazy(() => import("./pages/PaymentRequestsPage"));
const ReconciliationPage = lazy(() => import("./pages/ReconciliationPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const IntelligencePage = lazy(() => import("./pages/IntelligencePage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const AssetCommandPage = lazy(() => import("./pages/AssetCommandPage"));
const AssetFolderPage = lazy(() => import("./pages/AssetFolderPage"));
const MapViewPage = lazy(() => import("./pages/MapViewPage"));
const CardsPage = lazy(() => import("./pages/CardsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full" />
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <DashboardLayout>{children}</DashboardLayout>;
}

function AppRoutes() {
  const { user, activeClient, loading } = useAuth();
  // Use clientId as key to force remount all pages when client changes
  const clientKey = activeClient?.id || 'none';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
    </div>
  );

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><ExpensesPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/revenue" element={<ProtectedRoute><RevenuePage key={clientKey} /></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute><VendorsCustomersPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/chart-of-accounts" element={<ProtectedRoute><ChartOfAccountsPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/banking" element={<ProtectedRoute><BankingPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><TransactionsPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/payment-requests" element={<ProtectedRoute><PaymentRequestsPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/reconciliation" element={<ProtectedRoute><ReconciliationPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/budgets" element={<ProtectedRoute><BudgetsPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/cost-centers" element={<ProtectedRoute><CostCentersPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/assets" element={<ProtectedRoute><AssetCommandPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/asset-folders" element={<ProtectedRoute><AssetFolderPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/map" element={<ProtectedRoute><MapViewPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/cards" element={<ProtectedRoute><CardsPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistantPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/ai-tools" element={<ProtectedRoute><AIHubPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><IntelligencePage key={clientKey} /></ProtectedRoute>} />
      <Route path="/messaging" element={<ProtectedRoute><MessagingPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UsersPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage key={clientKey} /></ProtectedRoute>} />
      <Route path="/platform" element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
